/**
 * Batch 18-F15.2 — Privacy-safe evidence row builders for the
 * manager-only Training Evidence Drawer.
 *
 * Pure deterministic helpers (no I/O, no React import). Convert the
 * data the manager surface already holds in scope into a typed,
 * privacy-safe row shape that the drawer can render uniformly. The
 * trust class is encoded in `kind` and the human-readable provenance
 * is delegated to the F15.1 provenance-label helpers — so the trust
 * classes can never share labels.
 *
 * Coverage in this batch (data the parent surface already loads):
 *   - 'manual_completion'      from pending-verification rows
 *   - 'manager_verification'   from verified-session rows
 *
 * Coverage in this batch's TYPE UNION but no current input source:
 *   - 'manager_revocation'     — the parent surface does not load
 *                                training_completion_verifications;
 *                                a future batch can fan in revocation
 *                                data without changing the row type
 *
 * Explicitly NOT supported (load-bearing — see F15 §1 / §3 / §10):
 *   - extension evidence rows. The `kind` union does NOT include any
 *     'extension_*' literal so the type system refuses to express
 *     them. A future batch that wants to render extension evidence
 *     MUST extend this union AND add a new builder — there is no
 *     'mixed' or 'generic' kind to reuse.
 *   - provider / LMS callback rows. Same protection.
 *
 * Privacy rules — ALL builders:
 *   - never accept raw URLs, query strings, tokens, headers,
 *     signatures, provider payloads, or arbitrary metadata blobs
 *     (the input types do not expose those fields, so a caller can't
 *     accidentally smuggle them in)
 *   - never write to console
 *   - never persist anywhere
 *   - emit only fixed-shape `TrainingEvidenceDisplayRow` values
 *     whose `label` / `provenance` / `detail` are short summary
 *     strings safe to render verbatim
 */

import {
  getCompletionProvenanceLabel,
  getRevocationProvenanceLabel,
  getVerificationProvenanceLabel,
  MANAGER_REVERSAL_PROVENANCE_LABEL,
  MANUAL_COMPLETION_PROVENANCE_LABEL,
  SYSTEM_RECONCILIATION_PROVENANCE_LABEL,
  SYSTEM_SAFETY_REVERSAL_PROVENANCE_LABEL,
  VERIFICATION_PROVENANCE_LABEL,
} from './evidenceProvenanceLabels';

// ─── Row shape ──────────────────────────────────────────────────────────

export type TrainingEvidenceRowKind =
  | 'manager_verification'
  | 'manager_revocation'
  | 'manual_completion';

export type TrainingEvidenceRowVisibility = 'manager' | 'technician_summary';

export interface TrainingEvidenceDisplayRow {
  /** Row-stable identifier. Caller-supplied; typically the session id
   *  scoped by kind so a session that yields multiple kinds doesn't
   *  collide. */
  id: string;
  kind: TrainingEvidenceRowKind;
  /** Short user-facing title for the row. Names the action / state in
   *  manager-facing language. Never contains URLs / tokens / payloads. */
  label: string;
  /** Trust-class provenance line — always sourced from the F15.1
   *  helpers. The row builder MUST never construct this string
   *  ad-hoc; doing so would risk a trust-class label drift. */
  provenance: string;
  /** ISO timestamp the event occurred at (verification time, manual
   *  mark time, etc.). Nullable when not known. */
  occurredAt: string | null;
  /** Optional small detail line (e.g. artifact title, status). Short
   *  summary only; never raw URLs / notes / payloads. */
  detail: string | null;
  /** Visibility class — informs the drawer which rows are safe to
   *  show in technician-side summaries vs manager-only contexts.
   *  Currently every kind defaults to 'manager'; a future batch that
   *  surfaces a redacted summary on the technician side will use
   *  'technician_summary'. */
  visibility: TrainingEvidenceRowVisibility;
}

// ─── Row builders ───────────────────────────────────────────────────────

export interface ManagerVerificationRowInput {
  /** Stable identifier — typically `mv:<session_id>` so it can coexist
   *  with other kinds for the same session in one rendered list. */
  id: string;
  managerVerifiedAt: string | null;
  /** Free-text note captured at verification time. Rendered verbatim
   *  as `detail`; the note itself is short by UI convention and never
   *  carries URLs / tokens. */
  managerVerificationNote?: string | null;
  artifactTitle?: string | null;
}

export function buildManagerVerificationRow(
  input: ManagerVerificationRowInput,
): TrainingEvidenceDisplayRow {
  return {
    id: input.id,
    kind: 'manager_verification',
    label: 'Confirmed by Manager',
    provenance: getVerificationProvenanceLabel(),
    occurredAt: input.managerVerifiedAt ?? null,
    detail: composeDetail(
      input.artifactTitle ?? null,
      input.managerVerificationNote ?? null,
    ),
    visibility: 'manager',
  };
}

export interface ManagerRevocationRowInput {
  /** Stable identifier — typically `mr:<session_id>` or `<verification_id>`. */
  id: string;
  revokedAt: string | null;
  revocationNote?: string | null;
  autoRevoked?: boolean | null;
  oneShotReconciliation?: boolean | null;
  artifactTitle?: string | null;
}

export function buildManagerRevocationRow(
  input: ManagerRevocationRowInput,
): TrainingEvidenceDisplayRow {
  // Match the flavor-naming convention already used by
  // TrainingRevocationBadge so the drawer row and the badge agree.
  let label = 'Verification revoked';
  if (input.oneShotReconciliation) {
    label = 'System repaired stale verification';
  } else if (input.autoRevoked) {
    label = 'Auto-revoked after status changed';
  }
  return {
    id: input.id,
    kind: 'manager_revocation',
    label,
    provenance: getRevocationProvenanceLabel({
      autoRevoked: input.autoRevoked,
      oneShotReconciliation: input.oneShotReconciliation,
    }),
    occurredAt: input.revokedAt ?? null,
    detail: composeDetail(
      input.artifactTitle ?? null,
      input.revocationNote ?? null,
    ),
    visibility: 'manager',
  };
}

export interface ManualCompletionRowInput {
  /** Stable identifier — typically `mc:<session_id>`. */
  id: string;
  manualMarkedCompleteAt: string | null;
  /** Allowed values are `'completed'` and `'partial'` — anything else
   *  collapses to a generic detail. */
  completionStatus?: string | null;
  artifactTitle?: string | null;
}

export function buildManualCompletionRow(
  input: ManualCompletionRowInput,
): TrainingEvidenceDisplayRow {
  const statusDetail =
    input.completionStatus === 'partial'
      ? 'Partial completion'
      : input.completionStatus === 'completed'
      ? 'Completed'
      : null;
  return {
    id: input.id,
    kind: 'manual_completion',
    label: 'Technician marked complete',
    provenance: getCompletionProvenanceLabel('manual'),
    occurredAt: input.manualMarkedCompleteAt ?? null,
    detail: composeDetail(input.artifactTitle ?? null, statusDetail),
    visibility: 'manager',
  };
}

// ─── Internal helpers ──────────────────────────────────────────────────

/**
 * Compose a short detail line from at most two summary fragments.
 * Returns null when both fragments are null/empty. Never returns a
 * string longer than ~140 characters (truncates with an ellipsis).
 *
 * Inputs are assumed to be short summary text (artifact title,
 * verification note). Callers should not pass raw URLs / tokens /
 * full payloads — the input types do not expose those fields.
 */
function composeDetail(
  primary: string | null,
  secondary: string | null,
): string | null {
  const a = primary ? primary.trim() : '';
  const b = secondary ? secondary.trim() : '';
  if (!a && !b) return null;
  const joined = a && b ? `${a} — ${b}` : a || b;
  if (joined.length <= 140) return joined;
  return joined.slice(0, 137) + '…';
}

// ─── Trust-class label re-exports for drawer rendering ─────────────────
//
// The drawer renders the row.provenance directly, but tests also need
// to assert that every supported kind maps to exactly one F15.1
// constant. Re-exporting them here makes that table-driven.

export const TRAINING_EVIDENCE_PROVENANCE_BY_KIND: Record<
  TrainingEvidenceRowKind,
  ReadonlyArray<string>
> = {
  manager_verification: [VERIFICATION_PROVENANCE_LABEL],
  manager_revocation: [
    MANAGER_REVERSAL_PROVENANCE_LABEL,
    SYSTEM_SAFETY_REVERSAL_PROVENANCE_LABEL,
    SYSTEM_RECONCILIATION_PROVENANCE_LABEL,
  ],
  manual_completion: [MANUAL_COMPLETION_PROVENANCE_LABEL],
} as const;

// ─── Batch 18-F16: drawer summary helper ───────────────────────────────
//
// Pure deterministic counter for the manager-only Training Evidence
// Drawer's summary header. Counts rows per trust class. No I/O, no
// React, no DOM. The counts are derived strictly from row.kind — the
// helper never inspects detail / label / provenance, so a future
// label drift can't change what gets counted.
//
// Unknown kinds are silently dropped from the per-class counts but
// still tally toward `total` so a defensive caller can detect drift.

export interface TrainingEvidenceSummary {
  total: number;
  manualCompletion: number;
  managerVerification: number;
  managerRevocation: number;
}

export function summarizeTrainingEvidenceRows(
  rows: ReadonlyArray<TrainingEvidenceDisplayRow>,
): TrainingEvidenceSummary {
  let manualCompletion = 0;
  let managerVerification = 0;
  let managerRevocation = 0;
  for (const row of rows) {
    if (row.kind === 'manual_completion') manualCompletion += 1;
    else if (row.kind === 'manager_verification') managerVerification += 1;
    else if (row.kind === 'manager_revocation') managerRevocation += 1;
  }
  return {
    total: rows.length,
    manualCompletion,
    managerVerification,
    managerRevocation,
  };
}

// ─── Batch 18-F16.1: accessible trust-class labels ─────────────────────
//
// Plain-English description of the trust class for screen readers.
// Rendered in an sr-only span inside each drawer row so the trust
// class is announced before the visible label / detail / provenance.
// Pure deterministic helper — same input always returns the same
// string. No DOM, no React, no I/O.
//
// The strings here intentionally restate the trust-class name even
// when it duplicates the visible provenance Badge — visually-impaired
// users navigating row-by-row should hear the trust class first
// without depending on the badge's color cue.

export const SR_LABEL_MANUAL_COMPLETION =
  'Evidence trust class: self-attested manual completion.' as const;
export const SR_LABEL_MANAGER_VERIFICATION =
  'Evidence trust class: human-attested manager verification.' as const;
export const SR_LABEL_MANAGER_REVOCATION =
  'Evidence trust class: manager or system revocation.' as const;

export function screenReaderLabelForKind(
  kind: TrainingEvidenceRowKind,
): string {
  if (kind === 'manual_completion') return SR_LABEL_MANUAL_COMPLETION;
  if (kind === 'manager_verification') return SR_LABEL_MANAGER_VERIFICATION;
  if (kind === 'manager_revocation') return SR_LABEL_MANAGER_REVOCATION;
  // The TrainingEvidenceRowKind union is exhaustively covered above;
  // this branch exists only so a future kind addition produces a
  // visibly empty announcement (caught by the F16.1 source-pin) rather
  // than silently inheriting another class's label.
  return '';
}
